import { inject, Injectable } from '@angular/core';
import { ScScoreReCaptcha } from '@semantic-components/re-captcha';
import { firstValueFrom, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiResponse, ApiResponseStatusCode, APP_CONFIG, UserRepository } from '../core';


/**
* This services handles the authentication for Azure Maps services.
*/
@Injectable({ providedIn: 'root' })
export class AzureMapsAuthService {

	private readonly config = inject(APP_CONFIG);
	private readonly userRepo = inject(UserRepository);
	private readonly recaptchaSrv = inject(ScScoreReCaptcha);

	private readonly CAPTCHA_ACTION_AZURE_MAP = 'azure_maps_sas';

	private currentToken: string | null = null;
	private renewalTimer: ReturnType<typeof setTimeout> | null = null;

	async fetchToken(): Promise<string | null> {
		return firstValueFrom(
			from(this.recaptchaSrv.execute(this.CAPTCHA_ACTION_AZURE_MAP)).pipe(
				switchMap(token => this.userRepo.getAzureMapsSasToken(token)),
				map(res => this.handleApiResponse(res)),
				catchError(() => of(null))
			)
		);
	}

	private handleApiResponse<T>(response: ApiResponse<T>): T {
		if (response.statusCode === ApiResponseStatusCode.Success) return response.result as T;
		throw new Error('Generic error');
	}

	getCurrentToken(): string | null {
		return this.currentToken;
	}

	async initTokenRenewal(): Promise<string | null> {
		const token = await this.fetchToken();
		this.currentToken = token;
		this.scheduleRenewal();
		return token;
	}

	private scheduleRenewal(): void {
		this.clearRenewal();

		this.renewalTimer = setTimeout(async () => {
			try {
				const token = await this.fetchToken();
				this.currentToken = token;
				this.scheduleRenewal();
			} catch {
				this.scheduleRenewal();
			}
		}, this.config.azureMapsSasTokenValidity * 1000);
	}

	clearRenewal(): void {
		if (this.renewalTimer) {
			clearTimeout(this.renewalTimer);
			this.renewalTimer = null;
		}
	}

}